import { useTranslation } from 'react-i18next';

import { Badge, Button, Card, CardContent, EmptyState } from '@saas-kit/ui';

import type { WorkstationState } from './useWorkstation';

/** Counter grid shown before a shift is claimed. Counters already held by
 *  another staff member are shown disabled with a "claimed" badge so the
 *  claim button is never offered for a counter that is in use. */
export function CounterPicker({ ws }: { ws: WorkstationState }): JSX.Element {
  const { t } = useTranslation('qms');

  if (ws.counters.length === 0) {
    return <EmptyState title={t('workstation.picker.empty')} />;
  }

  return (
    <Card>
      <CardContent className="py-4">
        <p className="mb-3 text-sm font-medium text-foreground">
          {t('workstation.picker.title')}
        </p>
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {ws.counters.map((counter) => (
            <li
              key={counter.id}
              className="rounded-md border border-border p-4"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-foreground">{counter.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {counter.code}
                  </p>
                </div>
                {counter.is_claimed ? (
                  <Badge variant="secondary">
                    {t('workstation.picker.claimed')}
                  </Badge>
                ) : null}
              </div>
              {counter.is_claimed ? (
                <p className="mt-3 text-xs text-muted-foreground">
                  {counter.claimed_by_name
                    ? t('workstation.picker.in_use_by', {
                        name: counter.claimed_by_name,
                      })
                    : t('workstation.picker.in_use')}
                </p>
              ) : (
                <Button
                  className="mt-3 w-full"
                  disabled={ws.claim.isPending}
                  onClick={() => ws.claim.mutate(counter.id)}
                >
                  {t('workstation.picker.claim')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
